import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useUpdatePassword } from '@refinedev/core';

import {
  Button,
  Loading,
  Typography,
  useSnackbar,
} from '@rrexpress/core';
import { useDocumentTitle } from '@refinedev/react-router-v6';

import BluebirdImage from '@/assets/images/bluebird.svg';
import { RenderIf } from '@/components/conditional/render-if';
import { BaseForm } from '@/components/form/form';
import FormField from '@/components/form/form-field';
import { EyeCloseIcon, EyeIcon } from '@/components/icons';
import { useResetPassword } from '@/modules/auth/application/reset-password';
import { IResetPasswordTokenChekerParams } from '@/modules/auth/model/reset-password';

export const ResetPassword = () => {
  const setTitle = useDocumentTitle();
  const { token_code: tokenCode } = useParams<{ email: string, token_code: string }>();
  const { mutate, isLoading } = useUpdatePassword();
  const { resetPasswordTokenChecker, isLoading: checkerTokenLoading } = useResetPassword();
  const { show } = useSnackbar();
  const [isSuccess, setIsSuccess] = useState(false);
  const [isValidToken, setIsValidToken] = useState(false);
  const [email, setEmail] = useState();

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  async function handleOnSubmit(values: any) {
    if (values.password !== values.password_confirmation) {
      show('Password tidak sama', 'error');
      return;
    }

    try {
      mutate({
        ...values,
        email,
        token_code: tokenCode,
      }, {
        onSuccess: () => {
          show('Sukses reset password, silahkan login kembali', 'success');
          setIsSuccess(true);
        },
        onError: () => {
          show('Gagal reset password', 'error');
        },
      });
    } catch (error) {
      show('Gagal aktivasi akun', 'error');
    }
  }

  const footerButton = () => (
    <Button
      className="w-full"
      variant="primary"
    >
      Ganti Password
    </Button>
  );

  async function hadleTokenChecker() {
    try {
      await resetPasswordTokenChecker({
        token_code: tokenCode,
      } as IResetPasswordTokenChekerParams)
        .then((res) => setEmail(res?.data?.data?.email));
      setIsValidToken(true);
    } catch (error) {
      setIsValidToken(false);
    }
  }

  useEffect(() => {
    setTitle('Reset Password | Bluebird Kirim');

    return () => {
      setTitle('Bluebird Kirim');
    };
  }, []);

  useEffect(() => {
    if (tokenCode) {
      hadleTokenChecker();
    }
  }, [tokenCode]);

  return (
    <div className="container mx-auto h-[100vh] flex flex-col items-center justify-center">
      <div className="p-8 text-black lg:min-w-[30vw]">
        {(isLoading || checkerTokenLoading) && (
          <Loading
            variant="circular"
            size={40}
            speed={0.6}
            indicatorCap="round"
          />
        )}
        <div className={`flex flex-col justify-center text-center ${(isLoading || checkerTokenLoading) && 'hidden'}`}>
          <img src={BluebirdImage} className="w-[180px] mx-auto mb-4" />
          <RenderIf
            condition={isValidToken}
            fallback={(
              <div className="mb-4">
                <Typography level={6} fontWeight="regular">Link reset password tidak valid.</Typography>
              </div>
            )}
          >
            <RenderIf
              condition={isSuccess}
              fallback={(
                <>
                  <div className="mb-4">
                    <Typography level={3}>Transport Management System</Typography>
                  </div>
                  <div className="mb-4">
                    <Typography level={4} fontWeight="medium">Reset Password</Typography>
                  </div>
                  <div className="my-2 text-grey">
                    <BaseForm
                      onFinish={handleOnSubmit}
                      footer={footerButton()}
                    >
                      <div className="mb-4 flex gap-3 items-center justify-between">
                        <Typography level={5}>
                          <label htmlFor="password" className="font-medium">Password Baru</label>
                        </Typography>
                        <div className="w-[350px]">
                          <FormField
                            id="password"
                            component="form"
                            name="password"
                            componentProps={{
                              type: showPassword ? 'text' : 'password',
                              label: 'Password',
                              required: true,
                              rightIcon: (
                                <div onClick={() => setShowPassword((password) => !password)} className="cursor-pointer">
                                  {showPassword ? (
                                    <EyeIcon />
                                  ) : (
                                    <EyeCloseIcon />
                                  )}
                                </div>
                              ),
                            }}
                          />
                        </div>
                      </div>
                      <div className="mb-4 flex gap-3 items-center justify-between">
                        <Typography level={5}>
                          <label htmlFor="password_confirmation" className="font-medium">Konfirmasi Password Baru</label>
                        </Typography>
                        <div className="w-[350px]">
                          <FormField
                            id="password_confirmation"
                            component="form"
                            name="password_confirmation"
                            componentProps={{
                              type: showConfirmPassword ? 'text' : 'password',
                              label: 'Konfirmasi Password',
                              required: true,
                              rightIcon: (
                                <div onClick={() => setShowConfirmPassword((password) => !password)} className="cursor-pointer">
                                  {showConfirmPassword ? (
                                    <EyeIcon />
                                  ) : (
                                    <EyeCloseIcon />
                                  )}
                                </div>
                              ),
                            }}
                          />
                        </div>
                      </div>
                    </BaseForm>
                  </div>
                </>
              )}
            >
              <div className="mb-4">
                <Typography level={6} fontWeight="regular">Sukses reset password, silahkan login kembali</Typography>
              </div>
              <Link to="/auth/login">
                <Button
                  className="w-full"
                  variant="primary"
                >
                  Login
                </Button>
              </Link>
            </RenderIf>
          </RenderIf>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
